import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  RefreshCcw,
  Play,
  Plus,
  Upload,
  Trash2,
  ArrowLeft,
  X,
} from 'lucide-react'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { useAuth } from '../../contexts/AuthContext'

const MODE_OPTIONS = [
  { value: 'AML', label: 'AML / PEP only' },
  { value: 'DVS', label: 'DVS only' },
  { value: 'BOTH', label: 'DVS then AML' },
]

const STATUS_STYLES = {
  DRAFT: 'bg-gray-100 text-gray-800',
  QUEUED: 'bg-blue-100 text-blue-800',
  RUNNING: 'bg-indigo-100 text-indigo-800',
  COMPLETED: 'bg-green-100 text-green-800',
  FAILED: 'bg-red-100 text-red-800',
  CANCELLED: 'bg-amber-100 text-amber-900',
  PENDING: 'bg-gray-100 text-gray-700',
  PROCESSING: 'bg-blue-100 text-blue-800',
  DONE: 'bg-green-100 text-green-800',
  SKIPPED: 'bg-slate-100 text-slate-700',
  NEEDS_DOCS: 'bg-amber-100 text-amber-900',
}

function StatusPill({ status }) {
  const s = String(status || '—')
  return (
    <span
      className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${STATUS_STYLES[s] || 'bg-gray-100 text-gray-700'}`}
    >
      {s}
    </span>
  )
}

function modeLabel(mode) {
  return MODE_OPTIONS.find((o) => o.value === mode)?.label || mode
}

export default function BatchKyc() {
  const { can } = useAuth()
  const canManage = can('profile-safe/checks', 'manage')
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedId = searchParams.get('id')

  const [batches, setBatches] = useState([])
  const [detail, setDetail] = useState(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [message, setMessage] = useState(null)

  const [createOpen, setCreateOpen] = useState(false)
  const [newName, setNewName] = useState('')
  const [newDescription, setNewDescription] = useState('')
  const [newMode, setNewMode] = useState('AML')
  const [pendingMemberIds, setPendingMemberIds] = useState('')

  const [importOpen, setImportOpen] = useState(false)
  const [importTab, setImportTab] = useState('ids') // ids | csv
  const [memberIdsText, setMemberIdsText] = useState('')
  const [csvText, setCsvText] = useState('')

  const [docsItem, setDocsItem] = useState(null)
  const [idFrontFile, setIdFrontFile] = useState(null)
  const [idBackFile, setIdBackFile] = useState(null)
  const [docFields, setDocFields] = useState({
    given_name: '',
    middle_name: '',
    family_name: '',
    date_of_birth: '',
    document_type: 'drivers_licence',
    document_number: '',
    document_card_number: '',
    state_of_issue: 'NSW',
    country_code: 'AU',
  })

  const loadList = useCallback(async () => {
    const res = await authFetch(getLocalApiUrl('/api/profiles/batch-kyc'))
    const json = await res.json().catch(() => ({}))
    if (!res.ok || json.success === false) {
      throw new Error(json.error || `Failed to load batches (${res.status})`)
    }
    setBatches(json.data || [])
  }, [])

  const loadDetail = useCallback(async (id) => {
    if (!id) {
      setDetail(null)
      return
    }
    const res = await authFetch(getLocalApiUrl(`/api/profiles/batch-kyc/${encodeURIComponent(id)}`))
    const json = await res.json().catch(() => ({}))
    if (!res.ok || json.success === false) {
      throw new Error(json.error || `Failed to load batch (${res.status})`)
    }
    setDetail(json.data)
  }, [])

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      await loadList()
      if (selectedId) await loadDetail(selectedId)
      else setDetail(null)
    } catch (e) {
      setError(e.message || 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [loadList, loadDetail, selectedId])

  useEffect(() => {
    refresh()
  }, [refresh])

  useEffect(() => {
    if (!detail || detail.status !== 'RUNNING') return undefined
    const t = setInterval(() => {
      loadDetail(detail.id).catch(() => {})
      loadList().catch(() => {})
    }, 2500)
    return () => clearInterval(t)
  }, [detail?.id, detail?.status, loadDetail, loadList])

  // From Profile Search: open create with IDs ready to add after create
  useEffect(() => {
    const prefill = searchParams.get('memberIds')
    if (!prefill) return
    setPendingMemberIds(decodeURIComponent(prefill).split(',').filter(Boolean).join('\n'))
    setCreateOpen(true)
    const next = new URLSearchParams(searchParams)
    next.delete('memberIds')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  const openBatch = (id) => {
    const next = new URLSearchParams(searchParams)
    next.set('id', String(id))
    setSearchParams(next)
    setMessage(null)
  }

  const backToList = () => {
    const next = new URLSearchParams(searchParams)
    next.delete('id')
    setSearchParams(next)
    setDetail(null)
    setImportOpen(false)
    setMessage(null)
  }

  const createBatch = async () => {
    if (!canManage) return
    const name = newName.trim()
    if (!name) {
      setMessage({ type: 'err', text: 'Enter a batch name' })
      return
    }
    setBusy(true)
    setMessage(null)
    try {
      const res = await authFetch(getLocalApiUrl('/api/profiles/batch-kyc'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          description: newDescription.trim() || undefined,
          mode: newMode,
          source: 'SEARCH',
        }),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) throw new Error(json.error || 'Create failed')
      const batch = json.data
      setCreateOpen(false)
      setNewName('')
      setNewDescription('')
      setNewMode('AML')

      const ids = pendingMemberIds
        .split(/[\n,;\t]+/)
        .map((s) => s.trim())
        .filter(Boolean)
      setPendingMemberIds('')

      await loadList()
      openBatch(batch.id)

      if (ids.length) {
        const addRes = await authFetch(
          getLocalApiUrl(`/api/profiles/batch-kyc/${encodeURIComponent(batch.id)}/items`),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ memberIds: ids, source: 'SEARCH' }),
          }
        )
        const addJson = await addRes.json().catch(() => ({}))
        if (!addRes.ok || !addJson.success) {
          setMessage({
            type: 'err',
            text: `Batch created, but adding patrons failed: ${addJson.error || `HTTP ${addRes.status}`}`,
          })
          await loadDetail(batch.id)
          return
        }
        setDetail(addJson.data)
        await loadList()
        setMessage({
          type: 'ok',
          text: `Created “${batch.name}” and added ${addJson.added || 0} patron(s)${
            addJson.missing?.length ? ` (${addJson.missing.length} not found)` : ''
          }`,
        })
      } else {
        setMessage({ type: 'ok', text: `Created “${batch.name}” — add or import patrons next` })
      }
    } catch (e) {
      setMessage({ type: 'err', text: e.message || 'Create failed' })
    } finally {
      setBusy(false)
    }
  }

  const addMembers = async () => {
    if (!canManage || !detail?.id) return
    const ids = memberIdsText
      .split(/[\n,;\t]+/)
      .map((s) => s.trim())
      .filter(Boolean)
    if (!ids.length) return
    setBusy(true)
    setMessage(null)
    try {
      const res = await authFetch(
        getLocalApiUrl(`/api/profiles/batch-kyc/${encodeURIComponent(detail.id)}/items`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ memberIds: ids, source: 'SEARCH' }),
        }
      )
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) throw new Error(json.error || 'Add failed')
      setMemberIdsText('')
      setImportOpen(false)
      setDetail(json.data)
      await loadList()
      setMessage({
        type: 'ok',
        text: `Added ${json.added || 0} patron(s)${
          json.missing?.length ? ` — ${json.missing.length} ID(s) not found` : ''
        }`,
      })
    } catch (e) {
      setMessage({ type: 'err', text: e.message || 'Add failed' })
    } finally {
      setBusy(false)
    }
  }

  const importCsv = async () => {
    if (!canManage || !detail?.id || !csvText.trim()) return
    setBusy(true)
    setMessage(null)
    try {
      const res = await authFetch(
        getLocalApiUrl(`/api/profiles/batch-kyc/${encodeURIComponent(detail.id)}/import-csv`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ csvText }),
        }
      )
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) throw new Error(json.error || 'CSV import failed')
      setCsvText('')
      setImportOpen(false)
      setDetail(json.data)
      await loadList()
      setMessage({
        type: 'ok',
        text: `Imported ${json.added || 0} of ${json.parsedCount || 0} ID(s)${
          json.missing?.length ? ` — ${json.missing.length} not found` : ''
        }`,
      })
    } catch (e) {
      setMessage({ type: 'err', text: e.message || 'CSV import failed' })
    } finally {
      setBusy(false)
    }
  }

  const startBatch = async () => {
    if (!canManage || !detail?.id) return
    setBusy(true)
    setMessage(null)
    try {
      const res = await authFetch(
        getLocalApiUrl(`/api/profiles/batch-kyc/${encodeURIComponent(detail.id)}/start`),
        { method: 'POST' }
      )
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) throw new Error(json.error || 'Start failed')
      setDetail(json.data)
      await loadList()
      setMessage({ type: 'ok', text: 'Batch processing started' })
    } catch (e) {
      setMessage({ type: 'err', text: e.message || 'Start failed' })
    } finally {
      setBusy(false)
    }
  }

  const cancelBatch = async () => {
    if (!canManage || !detail?.id) return
    setBusy(true)
    try {
      const res = await authFetch(
        getLocalApiUrl(`/api/profiles/batch-kyc/${encodeURIComponent(detail.id)}/cancel`),
        { method: 'POST' }
      )
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) throw new Error(json.error || 'Cancel failed')
      setDetail(json.data)
      await loadList()
    } catch (e) {
      setMessage({ type: 'err', text: e.message || 'Cancel failed' })
    } finally {
      setBusy(false)
    }
  }

  const deleteBatch = async (batchId, batchName) => {
    if (!canManage || !batchId) return
    if (
      !window.confirm(
        `Delete “${batchName || `batch #${batchId}`}”?\n\nRemoves the batch and its patron list. Verification Queue rows already created are kept.`
      )
    ) {
      return
    }
    setBusy(true)
    setMessage(null)
    try {
      const res = await authFetch(
        getLocalApiUrl(`/api/profiles/batch-kyc/${encodeURIComponent(batchId)}`),
        { method: 'DELETE' }
      )
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) throw new Error(json.error || 'Delete failed')
      if (String(selectedId) === String(batchId)) backToList()
      await loadList()
      setMessage({ type: 'ok', text: 'Batch deleted' })
    } catch (e) {
      setMessage({ type: 'err', text: e.message || 'Delete failed' })
    } finally {
      setBusy(false)
    }
  }

  const items = detail?.items || []
  const progressLabel = useMemo(() => {
    if (!detail) return ''
    return `${detail.processedCount || 0}/${detail.totalCount || 0} done · ${detail.failedCount || 0} failed · ${detail.skippedCount || 0} skipped`
  }, [detail])

  const canEditItems = canManage && detail && !['RUNNING', 'QUEUED'].includes(detail.status)
  const needsDocsMode = detail && ['DVS', 'BOTH'].includes(String(detail.mode || '').toUpperCase())

  const openDocsPanel = (item) => {
    setDocsItem(item)
    const nameParts = String(item.displayName || '')
      .trim()
      .split(/\s+/)
      .filter(Boolean)
    setDocFields({
      given_name: nameParts[0] || '',
      middle_name: nameParts.length > 2 ? nameParts.slice(1, -1).join(' ') : '',
      family_name: nameParts.length > 1 ? nameParts[nameParts.length - 1] : '',
      date_of_birth: item.dateOfBirth || '',
      document_type: 'drivers_licence',
      document_number: '',
      document_card_number: '',
      state_of_issue: 'NSW',
      country_code: 'AU',
    })
    setIdFrontFile(null)
    setIdBackFile(null)
  }

  const saveItemDocuments = async () => {
    if (!canManage) {
      setMessage({ type: 'err', text: 'You need profile-safe/checks manage permission to save documents' })
      return
    }
    if (!detail?.id || !docsItem?.id) {
      setMessage({ type: 'err', text: 'Select a batch patron first' })
      return
    }
    const hasFile = Boolean(idFrontFile || idBackFile)
    const hasFields = Object.values(docFields || {}).some((v) => v != null && String(v).trim() !== '')
    const needsFront = String(docFields.document_type || 'drivers_licence') === 'drivers_licence'
    if (needsFront && !idFrontFile) {
      setMessage({ type: 'err', text: 'Upload the driver licence front image before saving documents' })
      return
    }
    if (!hasFile && !hasFields) {
      setMessage({ type: 'err', text: 'Upload an ID image or enter document fields, then save' })
      return
    }
    setBusy(true)
    setMessage(null)
    try {
      const fd = new FormData()
      if (idFrontFile) fd.append('idFront', idFrontFile)
      if (idBackFile) fd.append('idBack', idBackFile)
      Object.entries(docFields).forEach(([k, v]) => {
        if (v != null && String(v).trim() !== '') fd.append(k, String(v).trim())
      })
      const res = await authFetch(
        getLocalApiUrl(
          `/api/profiles/batch-kyc/${encodeURIComponent(detail.id)}/items/${encodeURIComponent(docsItem.id)}/documents`
        ),
        { method: 'POST', body: fd }
      )
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) throw new Error(json.error || 'Document save failed')
      setDetail(json.data)
      await loadList()
      if (json.ocrData) {
        setDocFields((prev) => ({
          ...prev,
          given_name: json.ocrData.given_name || prev.given_name,
          middle_name: json.ocrData.middle_name || prev.middle_name,
          family_name: json.ocrData.family_name || prev.family_name,
          date_of_birth: json.ocrData.date_of_birth || prev.date_of_birth,
          document_type: json.ocrData.document_type || prev.document_type,
          document_number: json.ocrData.document_number || prev.document_number,
          document_card_number: json.ocrData.document_card_number || prev.document_card_number,
          state_of_issue: json.ocrData.state_of_issue || prev.state_of_issue,
          country_code: json.ocrData.country_code || prev.country_code,
        }))
      }
      setMessage({
        type: json.ready ? 'ok' : 'err',
        text: json.ready
          ? `Documents saved for ${docsItem.displayName || docsItem.memberId} — ready for DVS`
          : `Saved — still missing some DVS fields. Check licence number/card/state and save again.`,
      })
      if (json.ready) {
        setDocsItem(null)
        setIdFrontFile(null)
        setIdBackFile(null)
      }
    } catch (e) {
      setMessage({ type: 'err', text: e.message || 'Document save failed' })
    } finally {
      setBusy(false)
    }
  }

  // ── Detail view ──────────────────────────────────────────────
  if (selectedId) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <button
          type="button"
          onClick={backToList}
          className="text-sm text-psg-teal hover:underline inline-flex items-center gap-1 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          All batches
        </button>

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-sm">
            {error}
          </div>
        )}
        {message && (
          <div
            className={`mb-4 rounded-lg border px-3 py-2 text-sm ${
              message.type === 'ok'
                ? 'border-green-200 bg-green-50 text-green-900'
                : 'border-red-200 bg-red-50 text-red-800'
            }`}
          >
            {message.text}
          </div>
        )}

        {!detail ? (
          <p className="text-sm text-gray-500">{loading ? 'Loading…' : 'Batch not found'}</p>
        ) : (
          <>
            <div className="rounded-lg border border-gray-200 bg-white p-5 mb-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h1 className="text-2xl font-semibold text-gray-900">{detail.name}</h1>
                    <StatusPill status={detail.status} />
                  </div>
                  {detail.description && (
                    <p className="text-sm text-gray-600 mt-2 whitespace-pre-wrap">{detail.description}</p>
                  )}
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500">
                    <span>{modeLabel(detail.mode)}</span>
                    <span>Source: {detail.source}</span>
                    {detail.createdBy && <span>By {detail.createdBy}</span>}
                    {detail.createdAt && <span>{new Date(detail.createdAt).toLocaleString()}</span>}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">{progressLabel}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" className="btn-secondary" onClick={refresh} disabled={loading || busy}>
                    <RefreshCcw className={`w-4 h-4 inline mr-1 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                  {canEditItems && (
                    <button
                      type="button"
                      className="btn-secondary"
                      disabled={busy}
                      onClick={() => {
                        setImportOpen(true)
                        setImportTab('ids')
                      }}
                    >
                      <Plus className="w-4 h-4 inline mr-1" />
                      Add / import
                    </button>
                  )}
                  {canManage && detail.status !== 'RUNNING' && (
                    <button type="button" className="btn-primary" disabled={busy || !items.length} onClick={startBatch}>
                      <Play className="w-4 h-4 inline mr-1" />
                      Start
                    </button>
                  )}
                  {canManage && detail.status === 'RUNNING' && (
                    <button type="button" className="btn-secondary" disabled={busy} onClick={cancelBatch}>
                      Cancel
                    </button>
                  )}
                  {canManage && detail.status !== 'RUNNING' && detail.status !== 'QUEUED' && (
                    <button
                      type="button"
                      className="btn-secondary border-red-200 text-red-700"
                      disabled={busy}
                      onClick={() => deleteBatch(detail.id, detail.name)}
                    >
                      <Trash2 className="w-4 h-4 inline mr-1" />
                      Delete
                    </button>
                  )}
                </div>
              </div>
            </div>

            {importOpen && canEditItems && (
              <div className="rounded-lg border border-teal-200 bg-teal-50/40 p-4 mb-4">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-medium text-gray-900">Add patrons to this batch</h2>
                  <button type="button" className="text-gray-500 hover:text-gray-800" onClick={() => setImportOpen(false)}>
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex gap-2 mb-3">
                  <button
                    type="button"
                    className={`px-3 py-1.5 text-sm rounded border ${
                      importTab === 'ids' ? 'bg-white border-teal-400 text-teal-900' : 'bg-transparent border-transparent text-gray-600'
                    }`}
                    onClick={() => setImportTab('ids')}
                  >
                    Member IDs
                  </button>
                  <button
                    type="button"
                    className={`px-3 py-1.5 text-sm rounded border ${
                      importTab === 'csv' ? 'bg-white border-teal-400 text-teal-900' : 'bg-transparent border-transparent text-gray-600'
                    }`}
                    onClick={() => setImportTab('csv')}
                  >
                    CSV import
                  </button>
                </div>
                {importTab === 'ids' ? (
                  <>
                    <textarea
                      className="w-full border rounded px-3 py-2 font-mono text-sm min-h-[120px] bg-white"
                      value={memberIdsText}
                      onChange={(e) => setMemberIdsText(e.target.value)}
                      placeholder={'One member ID per line\n37092\n152'}
                    />
                    <button
                      type="button"
                      className="btn-primary mt-2"
                      disabled={busy || !memberIdsText.trim()}
                      onClick={addMembers}
                    >
                      <Plus className="w-4 h-4 inline mr-1" />
                      Add to batch
                    </button>
                  </>
                ) : (
                  <>
                    <label className="block text-sm mb-2">
                      <span className="text-gray-700 font-medium">Upload a CSV file</span>
                      <input
                        type="file"
                        accept=".csv,text/csv,text/plain"
                        className="mt-1 block w-full text-sm"
                        onChange={(e) => {
                          const file = e.target.files?.[0]
                          if (!file) return
                          const reader = new FileReader()
                          reader.onload = () => setCsvText(String(reader.result || ''))
                          reader.readAsText(file)
                          e.target.value = ''
                        }}
                      />
                    </label>
                    <textarea
                      className="w-full border rounded px-3 py-2 font-mono text-sm min-h-[120px] bg-white"
                      value={csvText}
                      onChange={(e) => setCsvText(e.target.value)}
                      placeholder={'member_id\n37092\n152'}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Choose a file or paste member IDs. This is different from Add to batch, which only pastes IDs.
                    </p>
                    <button
                      type="button"
                      className="btn-primary mt-2"
                      disabled={busy || !csvText.trim()}
                      onClick={importCsv}
                    >
                      <Upload className="w-4 h-4 inline mr-1" />
                      Import CSV
                    </button>
                  </>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  After import, patron name, DOB and profile details appear in the table below.
                </p>
              </div>
            )}

            {docsItem && canEditItems && needsDocsMode && (
              <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-4 mb-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h2 className="font-medium text-gray-900">
                      Documents — {docsItem.displayName || docsItem.memberId || `item #${docsItem.id}`}
                    </h2>
                    <p className="text-xs text-gray-600 mt-0.5">
                      Upload ID front/back (OCR fills fields when available) or enter licence details manually.
                    </p>
                  </div>
                  <button type="button" className="text-gray-500 hover:text-gray-800" onClick={() => setDocsItem(null)}>
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <label className="text-sm block">
                    <span className="text-gray-700 font-medium">ID front *</span>
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      className="mt-1 block w-full text-sm"
                      onChange={(e) => setIdFrontFile(e.target.files?.[0] || null)}
                    />
                    {idFrontFile ? <span className="text-xs text-gray-500">{idFrontFile.name}</span> : null}
                  </label>
                  <label className="text-sm block">
                    <span className="text-gray-700 font-medium">ID back</span>
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      className="mt-1 block w-full text-sm"
                      onChange={(e) => setIdBackFile(e.target.files?.[0] || null)}
                    />
                    {idBackFile ? <span className="text-xs text-gray-500">{idBackFile.name}</span> : null}
                  </label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {[
                    ['given_name', 'Given name'],
                    ['middle_name', 'Middle name'],
                    ['family_name', 'Family name'],
                    ['date_of_birth', 'Date of birth'],
                    ['document_number', 'Document number'],
                    ['document_card_number', 'Card number (AU DL)'],
                    ['state_of_issue', 'State'],
                    ['country_code', 'Country'],
                  ].map(([key, label]) => (
                    <label key={key} className="text-sm block">
                      <span className="text-gray-600">{label}</span>
                      <input
                        className="mt-1 w-full border rounded px-2 py-1.5 bg-white"
                        value={docFields[key] || ''}
                        onChange={(e) => setDocFields((p) => ({ ...p, [key]: e.target.value }))}
                      />
                    </label>
                  ))}
                  <label className="text-sm block">
                    <span className="text-gray-600">Document type</span>
                    <select
                      className="mt-1 w-full border rounded px-2 py-1.5 bg-white"
                      value={docFields.document_type}
                      onChange={(e) => setDocFields((p) => ({ ...p, document_type: e.target.value }))}
                    >
                      <option value="drivers_licence">Driver&apos;s licence</option>
                      <option value="passport">Passport</option>
                      <option value="proof_of_age">Proof of age</option>
                      <option value="medicare">Medicare</option>
                    </select>
                  </label>
                </div>
                <div className="flex gap-2 mt-3">
                  <button
                    type="button"
                    className="btn-primary"
                    disabled={busy}
                    onClick={saveItemDocuments}
                  >
                    <Upload className="w-4 h-4 inline mr-1" />
                    Save documents
                  </button>
                  <button type="button" className="btn-secondary" onClick={() => setDocsItem(null)}>
                    Close
                  </button>
                </div>
              </div>
            )}

            <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
              <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
                <h2 className="text-sm font-medium text-gray-800">Patrons in batch ({items.length})</h2>
                {canEditItems && !importOpen && (
                  <button
                    type="button"
                    className="text-sm text-psg-teal hover:underline"
                    onClick={() => setImportOpen(true)}
                  >
                    Add / import
                  </button>
                )}
              </div>
              {needsDocsMode && (
                <p className="px-4 py-2 text-xs text-gray-500 border-b bg-white">
                  For DVS, patrons without licence details show as NEEDS_DOCS — use <span className="font-medium">Upload docs</span> on the row.
                  AML-only batches do not need documents.
                </p>
              )}
              <div className="overflow-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs text-gray-500">
                    <tr>
                      <th className="px-3 py-2">Member ID</th>
                      <th className="px-3 py-2">Name</th>
                      <th className="px-3 py-2">DOB</th>
                      <th className="px-3 py-2">Type</th>
                      <th className="px-3 py-2">Patron DVS</th>
                      <th className="px-3 py-2">Batch status</th>
                      <th className="px-3 py-2">Check DVS</th>
                      <th className="px-3 py-2">Check AML</th>
                      <th className="px-3 py-2">VQ</th>
                      <th className="px-3 py-2">Docs</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {items.map((it) => (
                      <tr key={it.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 font-mono text-xs">
                          {it.memberId || '—'}
                          {it.masterProfileId && (
                            <div className="text-[10px] text-gray-400 mt-0.5">{it.masterProfileId}</div>
                          )}
                        </td>
                        <td className="px-3 py-2 font-medium text-gray-900">
                          {it.displayName || '—'}
                          {it.errorMessage && (
                            <p className="text-xs text-red-700 font-normal mt-0.5 max-w-xs break-words">
                              {it.errorMessage}
                            </p>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-600">{it.dateOfBirth || '—'}</td>
                        <td className="px-3 py-2 text-xs">{it.profileType || '—'}</td>
                        <td className="px-3 py-2 text-xs">{it.patronDvsStatus || '—'}</td>
                        <td className="px-3 py-2">
                          <StatusPill status={it.status} />
                        </td>
                        <td className="px-3 py-2 text-xs">{it.dvsStatus || '—'}</td>
                        <td className="px-3 py-2 text-xs">{it.amlStatus || '—'}</td>
                        <td className="px-3 py-2">
                          {it.verificationQueueId ? (
                            <Link
                              to="/profile-safe/verification-queue"
                              className="text-psg-teal underline text-xs"
                            >
                              Open
                            </Link>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {canEditItems && needsDocsMode && it.status !== 'SKIPPED' ? (
                            <button
                              type="button"
                              className="text-xs text-psg-teal underline whitespace-nowrap"
                              onClick={() => openDocsPanel(it)}
                            >
                              {it.status === 'NEEDS_DOCS' ? 'Upload docs' : 'Docs / fields'}
                            </button>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    ))}
                    {!items.length && (
                      <tr>
                        <td colSpan={10} className="px-3 py-10 text-center text-gray-500">
                          No patrons yet.{' '}
                          {canEditItems ? (
                            <button
                              type="button"
                              className="text-psg-teal underline"
                              onClick={() => setImportOpen(true)}
                            >
                              Add member IDs or import a CSV
                            </button>
                          ) : (
                            'Add patrons when the batch is not running.'
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    )
  }

  // ── List view ────────────────────────────────────────────────
  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Batch KYC</h1>
          <p className="text-sm text-gray-600 mt-1">
            Create a batch, then add or import patrons. Processing runs one-by-one via the{' '}
            <Link to="/profile-safe/verification-queue" className="text-psg-teal underline">
              Verification Queue
            </Link>
            .
          </p>
        </div>
        <div className="flex gap-2">
          <button type="button" className="btn-secondary" onClick={refresh} disabled={loading || busy}>
            <RefreshCcw className={`w-4 h-4 inline mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          {canManage && (
            <button
              type="button"
              className="btn-primary"
              onClick={() => {
                setCreateOpen(true)
                setMessage(null)
              }}
            >
              <Plus className="w-4 h-4 inline mr-1" />
              New batch
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-sm">
          {error}
        </div>
      )}
      {message && (
        <div
          className={`mb-4 rounded-lg border px-3 py-2 text-sm ${
            message.type === 'ok'
              ? 'border-green-200 bg-green-50 text-green-900'
              : 'border-red-200 bg-red-50 text-red-800'
          }`}
        >
          {message.text}
        </div>
      )}

      {createOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Create Batch KYC</h2>
              <button
                type="button"
                className="text-gray-400 hover:text-gray-700"
                onClick={() => setCreateOpen(false)}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <label className="block text-sm">
              <span className="text-gray-700 font-medium">Name *</span>
              <input
                className="mt-1 w-full border rounded px-3 py-2"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. March AML refresh"
                autoFocus
              />
            </label>
            {message?.type === 'err' && createOpen ? (
              <p className="text-sm text-red-700">{message.text}</p>
            ) : null}
            <label className="block text-sm">
              <span className="text-gray-700 font-medium">Description</span>
              <textarea
                className="mt-1 w-full border rounded px-3 py-2 min-h-[80px]"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="Optional notes for operators"
              />
            </label>
            <label className="block text-sm">
              <span className="text-gray-700 font-medium">Checks to run</span>
              <select
                className="mt-1 w-full border rounded px-3 py-2"
                value={newMode}
                onChange={(e) => setNewMode(e.target.value)}
              >
                {MODE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
            {pendingMemberIds.trim() && (
              <p className="text-xs text-teal-800 bg-teal-50 border border-teal-100 rounded px-2 py-1.5">
                {pendingMemberIds.split(/[\n,;\t]+/).filter((s) => s.trim()).length} patron ID(s) from
                Profile Search will be added after create.
              </p>
            )}
            <p className="text-xs text-gray-500">
              After creating, open the batch to add member IDs or import a CSV. Patron details show in the
              batch list.
            </p>
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" className="btn-secondary" onClick={() => setCreateOpen(false)}>
                Cancel
              </button>
              <button type="button" className="btn-primary" disabled={busy} onClick={createBatch}>
                Create batch
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs text-gray-500">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Mode</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Progress</th>
              <th className="px-4 py-3">Created</th>
              <th className="px-4 py-3 w-20" />
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading && !batches.length ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                  Loading…
                </td>
              </tr>
            ) : !batches.length ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-gray-500">
                  No batches yet.{' '}
                  {canManage && (
                    <button type="button" className="text-psg-teal underline" onClick={() => setCreateOpen(true)}>
                      Create one
                    </button>
                  )}
                </td>
              </tr>
            ) : (
              batches.map((b) => (
                <tr
                  key={b.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => openBatch(b.id)}
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{b.name}</div>
                    {b.description && (
                      <div className="text-xs text-gray-500 mt-0.5 line-clamp-2 max-w-md">{b.description}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">{b.mode}</td>
                  <td className="px-4 py-3">
                    <StatusPill status={b.status} />
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">
                    {b.processedCount}/{b.totalCount}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {b.createdAt ? new Date(b.createdAt).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    {canManage && b.status !== 'RUNNING' && b.status !== 'QUEUED' && (
                      <button
                        type="button"
                        title="Delete batch"
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                        disabled={busy}
                        onClick={() => deleteBatch(b.id, b.name)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
